import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  createReceiptNumber,
  formatMoney,
  roundMoney,
  toAmount,
} from "../../services/billingService";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const toLocalDateTimeInput = (
  value = new Date()
) => {
  const date =
    value instanceof Date
      ? value
      : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const pad = (number) =>
    String(number).padStart(2, "0");

  return `${date.getFullYear()}-${pad(
    date.getMonth() + 1
  )}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";

  let date;

  if (typeof value?.toDate === "function") {
    date = value.toDate();
  } else if (value?.seconds) {
    date = new Date(
      value.seconds * 1000
    );
  } else {
    date = new Date(value);
  }

  if (
    !date ||
    Number.isNaN(date.getTime())
  ) {
    return clean(value) || "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPrimaryGuest = (stay = {}) => {
  if (
    Array.isArray(stay.guests) &&
    stay.guests.length > 0
  ) {
    return (
      stay.guests.find(
        (guest) =>
          guest?.isPrimary === true
      ) ||
      stay.guests[0]
    );
  }

  return {
    name:
      stay.guestName ||
      stay.name ||
      "Guest",

    phoneNumber:
      stay.phoneNumber ||
      stay.phone ||
      "",

    idType: stay.idType || "",
    idNumber: stay.idNumber || "",
  };
};

const getStayGuestCount = (stay = {}) => {
  if (
    Array.isArray(stay.guests) &&
    stay.guests.length > 0
  ) {
    return stay.guests.length;
  }

  return Math.max(
    1,
    toAmount(
      stay.adults ??
        stay.guestCount ??
        1
    )
  );
};

const maskIdNumber = (value) => {
  const idNumber = clean(value);

  if (!idNumber) return "-";

  if (idNumber.length <= 4) {
    return idNumber;
  }

  return `${"X".repeat(
    Math.max(
      4,
      idNumber.length - 4
    )
  )}${idNumber.slice(-4)}`;
};

const getPaymentStatus = ({
  balance,
  checkoutPayment,
}) => {
  const finalBalance = Math.max(
    0,
    roundMoney(
      toAmount(balance) -
        toAmount(checkoutPayment)
    )
  );

  if (finalBalance <= 0) {
    return {
      text: "Fully Paid",
      background: "#dcfce7",
      color: "#166534",
    };
  }

  if (toAmount(checkoutPayment) > 0) {
    return {
      text: "Part Payment",
      background: "#fef3c7",
      color: "#92400e",
    };
  }

  return {
    text: "Balance Pending",
    background: "#fee2e2",
    color: "#b91c1c",
  };
};

/* -------------------------------------------------------------------------- */
/*                              Reusable Field                                */
/* -------------------------------------------------------------------------- */

function Field({
  label,
  required = false,
  error = "",
  children,
}) {
  return (
    <label style={fieldStyle}>
      <div style={fieldLabelStyle}>
        {label}

        {required && (
          <span style={requiredStyle}>
            {" "}
            *
          </span>
        )}
      </div>

      {children}

      {error && (
        <div style={errorTextStyle}>
          {error}
        </div>
      )}
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/*                        Checkout Confirmation Modal                         */
/* -------------------------------------------------------------------------- */

function CheckoutConfirmModal({
  stay,
  bill,

  hotelId,
  hotelName,

  saving = false,

  onClose,
  onConfirm,
}) {
  const primaryGuest = useMemo(
    () => getPrimaryGuest(stay),
    [stay]
  );

  const currentBalance = Math.max(
    0,
    toAmount(bill?.balance)
  );

  const [form, setForm] = useState({
    // datetime-local ko LOCAL time chahiye. toISOString() UTC deta hai
    // aur India me 5:30 ghante peeche dikhakar false "before check-in" error de sakta tha.
    actualCheckOutAt:
      toLocalDateTimeInput(
        new Date()
      ),

    checkoutPayment:
      currentBalance > 0
        ? String(currentBalance)
        : "",

    paymentMode:
      currentBalance > 0
        ? "Cash"
        : "",

    transactionReference: "",

    allowPendingCheckout: false,

    pendingReason: "",

    managerNote: "",

    roomKeysReceived: true,

    guestSignatureReceived: true,

    idDocumentsVerified: true,

    roomCheckedForDamage: false,

    damageCharge: "",

    damageNote: "",

    sendRoomForCleaning: true,

    printInvoiceAfterCheckout: true,

    printReceiptAfterCheckout: false,
  });

  const [errors, setErrors] =
    useState({});

  const [localSaving, setLocalSaving] =
    useState(false);

  const isSaving =
    saving || localSaving;

  const checkoutPayment = Math.max(
    0,
    toAmount(form.checkoutPayment)
  );

  const damageCharge =
    form.roomCheckedForDamage
      ? Math.max(
          0,
          toAmount(form.damageCharge)
        )
      : 0;

  const finalGrandTotal = roundMoney(
    toAmount(bill?.grandTotal) +
      damageCharge
  );

  const finalPaid = roundMoney(
    toAmount(bill?.paid) +
      checkoutPayment
  );

  const finalBalance = roundMoney(
    Math.max(
      0,
      finalGrandTotal - finalPaid
    )
  );

  const excessPayment = roundMoney(
    Math.max(
      0,
      finalPaid - finalGrandTotal
    )
  );

  const paymentStatus =
    getPaymentStatus({
      balance:
        currentBalance +
        damageCharge,

      checkoutPayment,
    });

  /* ------------------------------------------------------------------------ */
  /*                         Reset Suggested Payment                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const requiredAmount = roundMoney(
      currentBalance +
        damageCharge
    );

    if (
      !form.allowPendingCheckout
    ) {
      setForm((current) => ({
        ...current,

        checkoutPayment:
          requiredAmount > 0
            ? String(requiredAmount)
            : "",
      }));
    }
  }, [
    currentBalance,
    damageCharge,
    form.allowPendingCheckout,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                              Form Updater                                 */
  /* ------------------------------------------------------------------------ */

  const updateForm = (
    field,
    value
  ) => {
    setForm((current) => {
      const next = {
        ...current,
        [field]: value,
      };

      if (
        field === "paymentMode" &&
        value === "Cash"
      ) {
        next.transactionReference = "";
      }

      if (
        field ===
          "allowPendingCheckout" &&
        value === false
      ) {
        next.pendingReason = "";
      }

      if (
        field ===
          "roomCheckedForDamage" &&
        value === false
      ) {
        next.damageCharge = "";
        next.damageNote = "";
      }

      return next;
    });

    setErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = {
        ...current,
      };

      delete next[field];

      return next;
    });
  };

  /* ------------------------------------------------------------------------ */
  /*                              Validation                                   */
  /* ------------------------------------------------------------------------ */

  const validate = () => {
    const nextErrors = {};

    const checkoutDate = new Date(
      form.actualCheckOutAt
    );

    const checkInDate = new Date(
      stay?.checkInAt
    );

    if (
      !form.actualCheckOutAt ||
      Number.isNaN(
        checkoutDate.getTime()
      )
    ) {
      nextErrors.actualCheckOutAt =
        "Valid checkout date/time required hai.";
    }

    // Early checkout is allowed. Expected checkout se compare nahi karte.
    // Sirf actual checkout actual check-in ke baad hona chahiye.
    if (
      !Number.isNaN(
        checkInDate.getTime()
      ) &&
      !Number.isNaN(
        checkoutDate.getTime()
      ) &&
      checkoutDate <= checkInDate
    ) {
      nextErrors.actualCheckOutAt =
        "Checkout time check-in ke baad hona chahiye.";
    }

    if (checkoutPayment < 0) {
      nextErrors.checkoutPayment =
        "Payment negative nahi ho sakta.";
    }

    if (
      checkoutPayment > 0 &&
      !clean(form.paymentMode)
    ) {
      nextErrors.paymentMode =
        "Payment mode required hai.";
    }

    if (
      checkoutPayment > 0 &&
      form.paymentMode !== "Cash" &&
      !clean(
        form.transactionReference
      )
    ) {
      nextErrors.transactionReference =
        "Online/Card payment ka transaction reference required hai.";
    }

    if (
      finalBalance > 0 &&
      !form.allowPendingCheckout
    ) {
      nextErrors.checkoutPayment =
        `${formatMoney(
          finalBalance
        )} balance abhi pending hai.`;
    }

    if (
      finalBalance > 0 &&
      form.allowPendingCheckout &&
      !clean(form.pendingReason)
    ) {
      nextErrors.pendingReason =
        "Pending balance checkout ka reason required hai.";
    }

    if (
      form.roomCheckedForDamage &&
      damageCharge > 0 &&
      !clean(form.damageNote)
    ) {
      nextErrors.damageNote =
        "Damage charge ka reason required hai.";
    }

    if (
      !form.roomKeysReceived
    ) {
      nextErrors.roomKeysReceived =
        "Room keys receive hone ki confirmation required hai.";
    }

    setErrors(nextErrors);

    return {
      valid:
        Object.keys(nextErrors)
          .length === 0,

      checkoutDate,
      errors: nextErrors,
    };
  };

  /* ------------------------------------------------------------------------ */
  /*                              Final Submit                                 */
  /* ------------------------------------------------------------------------ */

  const submitCheckout = async (
    event
  ) => {
    event.preventDefault();

    if (
      isSaving ||
      typeof onConfirm !== "function"
    ) {
      return;
    }

    const validation = validate();

    if (!validation.valid) {
      const firstError =
        Object.values(
          validation.errors
        )[0];

      alert(
        `Checkout complete nahi ho sakta.\n\n${
          firstError ||
          "Required details check karo."
        }`
      );

      return;
    }

    if (
      finalBalance > 0 &&
      form.allowPendingCheckout
    ) {
      const confirmed =
        window.confirm(
          `Warning ⚠️\n\n${formatMoney(
            finalBalance
          )} balance pending rahega.\n\nReason: ${clean(
            form.pendingReason
          )}\n\nKya Manager Override ke saath checkout karna hai?`
        );

      if (!confirmed) return;
    }

    if (excessPayment > 0) {
      const confirmed =
        window.confirm(
          `Payment total bill se ${formatMoney(
            excessPayment
          )} zyada hai.\n\nKya checkout continue karna hai?`
        );

      if (!confirmed) return;
    }

    const checkoutPaymentEntry =
      checkoutPayment > 0
        ? {
            amount:
              checkoutPayment,

            quantity: 1,

            rate:
              checkoutPayment,

            title:
              "Checkout Payment",

            note:
              clean(
                form.managerNote
              ) ||
              "Amount received during checkout",

            paymentMode:
              clean(
                form.paymentMode
              ),

            transactionReference:
              clean(
                form.transactionReference
              ),

            source:
              "checkout_payment",

            receiptNumber:
              createReceiptNumber(
                hotelId
              ),

            addedAt:
              validation.checkoutDate.toISOString(),
          }
        : null;

    const payload = {
      ...bill,

      hotelId:
        clean(hotelId),

      hotelName:
        clean(hotelName),

      stayId:
        stay?.stayId ||
        stay?.id ||
        "",

      room:
        clean(stay?.room),

      guestName:
        clean(
          primaryGuest?.name ||
            stay?.guestName
        ),

      phoneNumber:
        clean(
          primaryGuest?.phoneNumber ||
            stay?.phoneNumber
        ),

      actualCheckOutAt:
        validation.checkoutDate.toISOString(),

      checkoutPayment,

      checkoutPaymentEntry,

      damageCharge,

      damageNote:
        clean(form.damageNote),

      grandTotal:
        finalGrandTotal,

      paid:
        finalPaid,

      paidTotal:
        finalPaid,

      balance:
        finalBalance,

      excessPayment,

      paymentStatus:
        finalBalance <= 0
          ? "paid"
          : finalPaid > 0
          ? "partial"
          : "pending",

      allowPendingCheckout:
        Boolean(
          form.allowPendingCheckout &&
            finalBalance > 0
        ),

      pendingCheckoutOverride:
        Boolean(
          form.allowPendingCheckout &&
            finalBalance > 0
        ),

      pendingReason:
        finalBalance > 0
          ? clean(
              form.pendingReason
            )
          : "",

      managerNote:
        clean(
          form.managerNote
        ),

      roomKeysReceived:
        Boolean(
          form.roomKeysReceived
        ),

      guestSignatureReceived:
        Boolean(
          form.guestSignatureReceived
        ),

      idDocumentsVerified:
        Boolean(
          form.idDocumentsVerified
        ),

      roomCheckedForDamage:
        Boolean(
          form.roomCheckedForDamage
        ),

      sendRoomForCleaning:
        Boolean(
          form.sendRoomForCleaning
        ),

      printInvoiceAfterCheckout:
        Boolean(
          form.printInvoiceAfterCheckout
        ),

      printReceiptAfterCheckout:
        Boolean(
          form.printReceiptAfterCheckout &&
            checkoutPaymentEntry
        ),

      checkoutCompleted: true,
    };

    setLocalSaving(true);

    try {
      await onConfirm(payload);
    } catch (error) {
      console.error(
        "Checkout confirmation error:",
        error
      );

      alert(
        "Checkout complete nahi hua: " +
          (error?.message ||
            error)
      );
    } finally {
      setLocalSaving(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                            Keyboard Handling                              */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.key === "Escape" &&
        !isSaving
      ) {
        onClose?.();
      }

      if (
        (event.ctrlKey ||
          event.metaKey) &&
        event.key === "Enter"
      ) {
        event.preventDefault();

        document
          .getElementById(
            "checkout-confirm-form"
          )
          ?.requestSubmit();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () =>
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
  }, [isSaving, onClose]);

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                   */
  /* ------------------------------------------------------------------------ */

  if (!stay || !bill) {
    return null;
  }

  return (
    <div
      style={overlayStyle}
      onMouseDown={(event) => {
        if (
          event.target ===
            event.currentTarget &&
          !isSaving
        ) {
          onClose?.();
        }
      }}
    >
      <form
        id="checkout-confirm-form"
        onSubmit={submitCheckout}
        style={modalStyle}
      >
        <header style={headerStyle}>
          <div>
            <div style={titleRowStyle}>
              <h2 style={titleStyle}>
                Final Checkout
              </h2>

              <span
                style={{
                  ...paymentBadgeStyle,

                  background:
                    paymentStatus.background,

                  color:
                    paymentStatus.color,
                }}
              >
                {paymentStatus.text}
              </span>
            </div>

            <div style={subtitleStyle}>
              {hotelName || "Hotel"} •
              Room {stay.room || "-"}
            </div>
          </div>

          <button
            type="button"
            disabled={isSaving}
            onClick={() =>
              onClose?.()
            }
            style={closeButtonStyle}
          >
            ×
          </button>
        </header>

        <section style={guestSectionStyle}>
          <div style={avatarStyle}>
            {clean(
              primaryGuest?.name
            )
              .charAt(0)
              .toUpperCase() || "G"}
          </div>

          <div style={{ minWidth: 0 }}>
            <strong style={guestNameStyle}>
              {primaryGuest?.name ||
                stay.guestName ||
                "Guest"}
            </strong>

            <div style={guestMetaStyle}>
              📞{" "}
              {primaryGuest?.phoneNumber ||
                stay.phoneNumber ||
                "-"}{" "}
              • 👥{" "}
              {getStayGuestCount(stay)}{" "}
              Guest
              {getStayGuestCount(stay) !==
              1
                ? "s"
                : ""}
            </div>

            <div style={guestIdStyle}>
              🪪{" "}
              {primaryGuest?.idType ||
                stay.idType ||
                "ID"}{" "}
              •{" "}
              {maskIdNumber(
                primaryGuest?.idNumber ||
                  stay.idNumber
              )}
            </div>
          </div>
        </section>

        <section style={stayGridStyle}>
          <InfoBox
            label="A&D Serial"
            value={
              stay.adRegisterSerialNo ||
              "-"
            }
          />

          <InfoBox
            label="Check-In"
            value={formatDateTime(
              stay.checkInAt
            )}
          />

          <InfoBox
            label="Expected Checkout"
            value={formatDateTime(
              stay.expectedCheckOutAt
            )}
          />

          <InfoBox
            label="Stay Type"
            value={
              stay.stayType || "-"
            }
          />

          <InfoBox
            label="Booking Source"
            value={
              stay.bookingSource ||
              "-"
            }
          />

          <InfoBox
            label="Invoice Number"
            value={
              bill.invoiceNumber ||
              stay.invoiceNumber ||
              "Will generate"
            }
          />
        </section>

        <section style={billSectionStyle}>
          <h3 style={sectionTitleStyle}>
            Final Bill Summary
          </h3>

          <BillRow
            label="Room Rent"
            value={bill.roomRent}
          />

          <BillRow
            label="Food Charges"
            value={bill.food}
          />

          <BillRow
            label="Laundry"
            value={bill.laundry}
          />

          <BillRow
            label="Extra Charges"
            value={bill.extra}
          />

          <BillRow
            label="Discount"
            value={-toAmount(
              bill.discount
            )}
          />

          <BillRow
            label={`GST (${toAmount(
              bill.gstRate
            )}%)`}
            value={bill.gstAmount}
          />

          {damageCharge > 0 && (
            <BillRow
              label="Damage Charge"
              value={damageCharge}
              warning
            />
          )}

          <div style={dividerStyle} />

          <BillRow
            label="Grand Total"
            value={finalGrandTotal}
            strong
          />

          <BillRow
            label="Already Paid"
            value={bill.paid}
          />

          <BillRow
            label="Checkout Payment"
            value={checkoutPayment}
          />

          <BillRow
            label="Final Balance"
            value={finalBalance}
            strong
            danger={finalBalance > 0}
          />

          {excessPayment > 0 && (
            <BillRow
              label="Excess Payment"
              value={excessPayment}
              warning
            />
          )}
        </section>

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>
            Checkout Payment
          </h3>

          <div style={formGridStyle}>
            <Field
              label="Actual Checkout Time"
              required
              error={
                errors.actualCheckOutAt
              }
            >
              <input
                type="datetime-local"
                value={
                  form.actualCheckOutAt
                }
                disabled={isSaving}
                onChange={(event) =>
                  updateForm(
                    "actualCheckOutAt",
                    event.target.value
                  )
                }
                style={{
                  ...inputStyle,
                  ...getErrorStyle(
                    errors.actualCheckOutAt
                  ),
                }}
              />
            </Field>

            <Field
              label="Amount Received Now"
              error={
                errors.checkoutPayment
              }
            >
              <input
                type="number"
                min="0"
                value={
                  form.checkoutPayment
                }
                disabled={isSaving}
                onChange={(event) =>
                  updateForm(
                    "checkoutPayment",
                    event.target.value
                  )
                }
                style={{
                  ...inputStyle,
                  ...getErrorStyle(
                    errors.checkoutPayment
                  ),
                }}
                placeholder="₹"
              />
            </Field>

            {checkoutPayment > 0 && (
              <Field
                label="Payment Mode"
                required
                error={
                  errors.paymentMode
                }
              >
                <select
                  value={
                    form.paymentMode
                  }
                  disabled={isSaving}
                  onChange={(event) =>
                    updateForm(
                      "paymentMode",
                      event.target.value
                    )
                  }
                  style={{
                    ...inputStyle,
                    ...getErrorStyle(
                      errors.paymentMode
                    ),
                  }}
                >
                  <option>Cash</option>
                  <option>UPI</option>
                  <option>Card</option>
                  <option>
                    Bank Transfer
                  </option>
                  <option>Fab</option>
                  <option>Brevistay</option>
                  <option>Other OTA</option>
                </select>
              </Field>
            )}

            {checkoutPayment > 0 &&
              form.paymentMode !==
                "Cash" && (
                <Field
                  label="Transaction Reference"
                  required
                  error={
                    errors.transactionReference
                  }
                >
                  <input
                    value={
                      form.transactionReference
                    }
                    disabled={isSaving}
                    onChange={(event) =>
                      updateForm(
                        "transactionReference",
                        event.target.value
                      )
                    }
                    style={{
                      ...inputStyle,
                      ...getErrorStyle(
                        errors.transactionReference
                      ),
                    }}
                    placeholder="UPI / Card / Bank code"
                  />
                </Field>
              )}
          </div>
        </section>

        <section style={sectionStyle}>
          <label style={optionCardStyle}>
            <input
              type="checkbox"
              checked={
                form.roomCheckedForDamage
              }
              disabled={isSaving}
              onChange={(event) =>
                updateForm(
                  "roomCheckedForDamage",
                  event.target.checked
                )
              }
            />

            Room damage / missing item charge add karein
          </label>

          {form.roomCheckedForDamage && (
            <div style={formGridStyle}>
              <Field label="Damage Charge">
                <input
                  type="number"
                  min="0"
                  value={
                    form.damageCharge
                  }
                  disabled={isSaving}
                  onChange={(event) =>
                    updateForm(
                      "damageCharge",
                      event.target.value
                    )
                  }
                  style={inputStyle}
                  placeholder="₹"
                />
              </Field>

              <Field
                label="Damage Details"
                required={
                  damageCharge > 0
                }
                error={
                  errors.damageNote
                }
              >
                <input
                  value={
                    form.damageNote
                  }
                  disabled={isSaving}
                  onChange={(event) =>
                    updateForm(
                      "damageNote",
                      event.target.value
                    )
                  }
                  style={{
                    ...inputStyle,
                    ...getErrorStyle(
                      errors.damageNote
                    ),
                  }}
                  placeholder="Damage / missing item details"
                />
              </Field>
            </div>
          )}
        </section>

        {finalBalance > 0 && (
          <section style={pendingSectionStyle}>
            <label style={pendingToggleStyle}>
              <input
                type="checkbox"
                checked={
                  form.allowPendingCheckout
                }
                disabled={isSaving}
                onChange={(event) =>
                  updateForm(
                    "allowPendingCheckout",
                    event.target.checked
                  )
                }
              />

              Manager Override: Pending balance ke saath checkout
            </label>

            {form.allowPendingCheckout && (
              <Field
                label="Pending Checkout Reason"
                required
                error={
                  errors.pendingReason
                }
              >
                <textarea
                  value={
                    form.pendingReason
                  }
                  disabled={isSaving}
                  onChange={(event) =>
                    updateForm(
                      "pendingReason",
                      event.target.value
                    )
                  }
                  style={{
                    ...inputStyle,
                    minHeight: 75,
                    resize: "vertical",
                    ...getErrorStyle(
                      errors.pendingReason
                    ),
                  }}
                  placeholder="Example: Company payment pending / Owner approval / Guest will pay later"
                />
              </Field>
            )}
          </section>
        )}

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>
            Checkout Checklist
          </h3>

          <div style={checklistGridStyle}>
            <ChecklistItem
              label="Room Keys Received"
              checked={
                form.roomKeysReceived
              }
              disabled={isSaving}
              error={
                errors.roomKeysReceived
              }
              onChange={(checked) =>
                updateForm(
                  "roomKeysReceived",
                  checked
                )
              }
            />

            <ChecklistItem
              label="Guest Signature Received"
              checked={
                form.guestSignatureReceived
              }
              disabled={isSaving}
              onChange={(checked) =>
                updateForm(
                  "guestSignatureReceived",
                  checked
                )
              }
            />

            <ChecklistItem
              label="ID Documents Verified"
              checked={
                form.idDocumentsVerified
              }
              disabled={isSaving}
              onChange={(checked) =>
                updateForm(
                  "idDocumentsVerified",
                  checked
                )
              }
            />

            <ChecklistItem
              label="Move Room to Cleaning List"
              checked={
                form.sendRoomForCleaning
              }
              disabled={isSaving}
              onChange={(checked) =>
                updateForm(
                  "sendRoomForCleaning",
                  checked
                )
              }
            />

            <ChecklistItem
              label="Print Invoice after Checkout"
              checked={
                form.printInvoiceAfterCheckout
              }
              disabled={isSaving}
              onChange={(checked) =>
                updateForm(
                  "printInvoiceAfterCheckout",
                  checked
                )
              }
            />

            <ChecklistItem
              label="Print Payment Receipt"
              checked={
                form.printReceiptAfterCheckout
              }
              disabled={
                isSaving ||
                checkoutPayment <= 0
              }
              onChange={(checked) =>
                updateForm(
                  "printReceiptAfterCheckout",
                  checked
                )
              }
            />
          </div>

          <Field label="Manager Note">
            <textarea
              value={
                form.managerNote
              }
              disabled={isSaving}
              onChange={(event) =>
                updateForm(
                  "managerNote",
                  event.target.value
                )
              }
              style={{
                ...inputStyle,
                minHeight: 75,
                resize: "vertical",
              }}
              placeholder="Guest feedback, checkout note, room condition..."
            />
          </Field>
        </section>

        <footer style={footerStyle}>
          <div style={shortcutStyle}>
            Ctrl + Enter: Checkout • Esc:
            Close
          </div>

          <div style={footerButtonsStyle}>
            <button
              type="button"
              disabled={isSaving}
              onClick={() =>
                onClose?.()
              }
              style={cancelButtonStyle}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSaving}
              style={{
                ...checkoutButtonStyle,

                opacity: isSaving
                  ? 0.6
                  : 1,

                cursor: isSaving
                  ? "not-allowed"
                  : "pointer",
              }}
            >
              {isSaving
                ? "Checkout Saving..."
                : finalBalance > 0
                ? `Checkout with ${formatMoney(
                    finalBalance
                  )} Pending`
                : "Confirm Final Checkout ✅"}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Small Components                               */
/* -------------------------------------------------------------------------- */

function InfoBox({
  label,
  value,
}) {
  return (
    <div style={infoBoxStyle}>
      <span style={infoLabelStyle}>
        {label}
      </span>

      <strong style={infoValueStyle}>
        {value}
      </strong>
    </div>
  );
}

function BillRow({
  label,
  value,
  strong = false,
  danger = false,
  warning = false,
}) {
  return (
    <div
      style={{
        ...billRowStyle,

        fontSize: strong
          ? 17
          : 13,

        fontWeight: strong
          ? 900
          : 600,

        color: danger
          ? "#dc2626"
          : warning
          ? "#c2410c"
          : "#334155",
      }}
    >
      <span>{label}</span>

      <span>
        {formatMoney(value)}
      </span>
    </div>
  );
}

function ChecklistItem({
  label,
  checked,
  disabled,
  error = "",
  onChange,
}) {
  return (
    <label
      style={{
        ...checklistItemStyle,

        borderColor: error
          ? "#ef4444"
          : checked
          ? "#86efac"
          : "#cbd5e1",

        background: error
          ? "#fef2f2"
          : checked
          ? "#f0fdf4"
          : "#ffffff",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) =>
          onChange?.(
            event.target.checked
          )
        }
      />

      <span>
        {label}

        {error && (
          <small style={checklistErrorStyle}>
            {error}
          </small>
        )}
      </span>
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const getErrorStyle = (error) =>
  error
    ? {
        borderColor: "#dc2626",
        boxShadow:
          "0 0 0 2px rgba(220,38,38,.08)",
      }
    : {};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 24000,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  padding: 12,

  background:
    "rgba(15,23,42,.78)",
};

const modalStyle = {
  width: "min(920px,100%)",
  maxHeight: "96vh",

  padding: 20,

  overflowY: "auto",

  borderRadius: 21,

  background: "#ffffff",

  boxSizing: "border-box",

  boxShadow:
    "0 28px 90px rgba(0,0,0,.35)",
};

const headerStyle = {
  position: "sticky",
  top: -20,
  zIndex: 20,

  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 12,

  margin: "-20px -20px 16px",
  padding: "17px 20px",

  background:
    "rgba(255,255,255,.98)",

  borderBottom:
    "1px solid #e2e8f0",

  backdropFilter: "blur(12px)",
};

const titleRowStyle = {
  display: "flex",
  alignItems: "center",

  gap: 9,
  flexWrap: "wrap",
};

const titleStyle = {
  margin: 0,
  color: "#0f172a",
};

const subtitleStyle = {
  marginTop: 5,

  color: "#64748b",

  fontSize: 13,
  fontWeight: 700,
};

const paymentBadgeStyle = {
  padding: "5px 9px",

  borderRadius: 999,

  fontSize: 10,
  fontWeight: 900,
};

const closeButtonStyle = {
  width: 39,
  height: 39,

  border: 0,
  borderRadius: 10,

  background: "#f1f5f9",
  color: "#334155",

  fontSize: 27,
  lineHeight: 1,

  cursor: "pointer",
};

const guestSectionStyle = {
  display: "flex",
  alignItems: "center",

  gap: 11,

  padding: 13,

  borderRadius: 13,

  border:
    "1px solid #cbd5e1",

  background: "#f8fafc",
};

const avatarStyle = {
  width: 46,
  height: 46,

  flex: "0 0 auto",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  borderRadius: "50%",

  background: "#dbeafe",
  color: "#1d4ed8",

  fontSize: 18,
  fontWeight: 900,
};

const guestNameStyle = {
  color: "#0f172a",

  fontSize: 16,
};

const guestMetaStyle = {
  marginTop: 4,

  color: "#64748b",

  fontSize: 12,
};

const guestIdStyle = {
  marginTop: 3,

  color: "#475569",

  fontSize: 10,
};

const stayGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(145px,1fr))",

  gap: 9,

  marginTop: 13,
};

const infoBoxStyle = {
  padding: 10,

  borderRadius: 10,

  border:
    "1px solid #e2e8f0",

  background: "#f8fafc",
};

const infoLabelStyle = {
  display: "block",

  marginBottom: 4,

  color: "#64748b",

  fontSize: 9,
  fontWeight: 800,

  textTransform: "uppercase",
};

const infoValueStyle = {
  display: "block",

  color: "#334155",

  fontSize: 11,
  fontWeight: 900,

  overflowWrap: "anywhere",
};

const sectionStyle = {
  marginTop: 14,
  padding: 14,

  borderRadius: 14,

  border:
    "1px solid #e2e8f0",

  background: "#ffffff",
};

const billSectionStyle = {
  ...sectionStyle,
  background: "#f8fafc",
};

const sectionTitleStyle = {
  margin: "0 0 11px",

  color: "#0f172a",

  fontSize: 16,
};

const billRowStyle = {
  display: "flex",
  justifyContent: "space-between",

  gap: 12,

  padding: "5px 0",
};

const dividerStyle = {
  height: 1,

  margin: "7px 0",

  background: "#cbd5e1",
};

const formGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(210px,1fr))",

  gap: 11,

  marginTop: 9,
};

const fieldStyle = {
  display: "block",
  width: "100%",
};

const fieldLabelStyle = {
  marginBottom: 6,

  color: "#334155",

  fontSize: 12,
  fontWeight: 800,
};

const requiredStyle = {
  color: "#dc2626",
};

const inputStyle = {
  width: "100%",
  minHeight: 43,

  padding: "10px 11px",

  border:
    "1px solid #cbd5e1",

  borderRadius: 10,

  background: "#ffffff",
  color: "#0f172a",

  boxSizing: "border-box",

  fontSize: 13,

  outline: "none",
};

const errorTextStyle = {
  marginTop: 5,

  color: "#dc2626",

  fontSize: 10,
  fontWeight: 700,
};

const optionCardStyle = {
  display: "flex",
  alignItems: "center",

  gap: 8,

  padding: 10,

  borderRadius: 10,

  border:
    "1px solid #cbd5e1",

  background: "#f8fafc",

  color: "#334155",

  fontSize: 12,
  fontWeight: 800,
};

const pendingSectionStyle = {
  ...sectionStyle,

  borderColor: "#fdba74",

  background: "#fff7ed",
};

const pendingToggleStyle = {
  display: "flex",
  alignItems: "center",

  gap: 8,

  marginBottom: 11,

  color: "#9a3412",

  fontSize: 12,
  fontWeight: 900,
};

const checklistGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(220px,1fr))",

  gap: 9,

  marginBottom: 12,
};

const checklistItemStyle = {
  display: "flex",
  alignItems: "flex-start",

  gap: 8,

  padding: 10,

  border:
    "1px solid #cbd5e1",

  borderRadius: 10,

  color: "#334155",

  fontSize: 11,
  fontWeight: 800,
};

const checklistErrorStyle = {
  display: "block",

  marginTop: 3,

  color: "#dc2626",

  fontSize: 9,
};

const footerStyle = {
  position: "sticky",
  bottom: -20,
  zIndex: 20,

  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",

  gap: 12,
  flexWrap: "wrap",

  margin: "17px -20px -20px",
  padding: "14px 20px",

  borderTop:
    "1px solid #e2e8f0",

  background:
    "rgba(255,255,255,.98)",

  backdropFilter: "blur(12px)",
};

const shortcutStyle = {
  color: "#64748b",
  fontSize: 10,
};

const footerButtonsStyle = {
  display: "flex",

  gap: 9,
  flexWrap: "wrap",
};

const cancelButtonStyle = {
  padding: "11px 16px",

  border:
    "1px solid #cbd5e1",

  borderRadius: 10,

  background: "#ffffff",
  color: "#334155",

  fontWeight: 800,

  cursor: "pointer",
};

const checkoutButtonStyle = {
  padding: "11px 17px",

  border: 0,
  borderRadius: 10,

  background: "#ea580c",
  color: "#ffffff",

  fontWeight: 900,
};

export default CheckoutConfirmModal;